import { memo, useEffect, useMemo, useRef, useState } from 'react';
import type { PlanWithAgePremiums } from '@/components/widgets/PlansSection';
import type { MonthlyPremiumProps } from './types';
import { AgeRangePremium } from './AgeRangePremium';
import { EmployerContributionPremium } from './EmployerContributionPremium';
import { SingleMonthlyPremium } from './SingleMonthlyPremium';

export const MonthlyPremium = memo(({
    plan,
    rightSection,
    isStateCountrySearch = false,
    premiumAge18,
    premiumAge65,
    familyAges = [],
    employerContributionAmount = null,
    hideViewAllRatesButton = false,
    allRatesModalCompare,
}: MonthlyPremiumProps) => {
    const rowRef = useRef<HTMLDivElement | null>(null);
    const rightRef = useRef<HTMLDivElement | null>(null);
    const [isRightWrapped, setIsRightWrapped] = useState(false);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        const isDesktop = () => window.matchMedia('(min-width: 1024px)').matches; // Tailwind lg

        const measure = () => {
            if (!isDesktop() || !rightSection) {
                setIsRightWrapped(false);
                return;
            }
            const rowEl = rowRef.current;
            const rightEl = rightRef.current;
            if (!rowEl || !rightEl) return;

            const rowRect = rowEl.getBoundingClientRect();
            const rightRect = rightEl.getBoundingClientRect();
            // If right section extends beyond the row, wrap it to a new line.
            setIsRightWrapped(rightRect.right > rowRect.right + 1);
        };

        const raf = window.requestAnimationFrame(measure);
        window.addEventListener('resize', measure);
        return () => {
            window.cancelAnimationFrame(raf);
            window.removeEventListener('resize', measure);
        };
    }, [
        rightSection,
        isStateCountrySearch,
        premiumAge18,
        premiumAge65,
        familyAges.length,
        plan.premium,
        employerContributionAmount,
    ]);
    if (isStateCountrySearch && (premiumAge18 || premiumAge65)) {
        return (
            <AgeRangePremium
                rowRef={rowRef}
                rightSection={rightSection}
                rightRef={rightRef}
                isRightWrapped={isRightWrapped}
                plan={plan as PlanWithAgePremiums}
                premiumAge18={premiumAge18}
                premiumAge65={premiumAge65}
                hideViewAllRatesButton={hideViewAllRatesButton}
                allRatesModalCompare={allRatesModalCompare}
            />
        );
    }

    const premiumNumber = useMemo(() => {
        const cleaned = String(plan.premium ?? '').replace(/[^0-9.]/g, '');
        const n = parseFloat(cleaned);
        return Number.isFinite(n) ? n : null;
    }, [plan.premium]);

    const isFamilyPlan = useMemo(() => familyAges.length > 1, [familyAges.length]);

    const hasEmployerContribution =
        premiumNumber != null &&
        premiumNumber > 0 &&
        employerContributionAmount != null &&
        employerContributionAmount > 0;

    if (hasEmployerContribution) {
        const employerPay = Math.min(employerContributionAmount!, premiumNumber!);
        const employeePay = Math.max(premiumNumber! - employerContributionAmount!, 0);
        const available = Math.max(employerContributionAmount! - premiumNumber!, 0);
        const isOnExchangeOnly = Boolean(plan.on_market) && !Boolean(plan.off_market);
        const employeeTaxLabel = isOnExchangeOnly ? 'Post-tax, Per Month' : 'Pre-tax, Per Month';

        return (
            <EmployerContributionPremium
                rowRef={rowRef}
                rightSection={rightSection}
                rightRef={rightRef}
                isRightWrapped={isRightWrapped}
                isFamilyPlan={isFamilyPlan}
                premiumNumber={premiumNumber!}
                employerContributionAmount={employerContributionAmount!}
                employerPay={employerPay}
                employeePay={employeePay}
                available={available}
                employeeTaxLabel={employeeTaxLabel}
            />
        );
    }

    return (
        <SingleMonthlyPremium
            rowRef={rowRef}
            rightSection={rightSection}
            rightRef={rightRef}
            isRightWrapped={isRightWrapped}
            isFamilyPlan={isFamilyPlan}
            premiumNumber={premiumNumber}
        />
    );
}, (prevProps, nextProps) => {
    return (
        prevProps.plan.id === nextProps.plan.id &&
        prevProps.isStateCountrySearch === nextProps.isStateCountrySearch &&
        prevProps.premiumAge18 === nextProps.premiumAge18 &&
        prevProps.premiumAge65 === nextProps.premiumAge65 &&
        prevProps.employerContributionAmount === nextProps.employerContributionAmount &&
        prevProps.familyAges?.length === nextProps.familyAges?.length &&
        JSON.stringify(prevProps.familyAges) === JSON.stringify(nextProps.familyAges) &&
        prevProps.hideViewAllRatesButton === nextProps.hideViewAllRatesButton &&
        prevProps.allRatesModalCompare?.onToggleCompare === nextProps.allRatesModalCompare?.onToggleCompare &&
        prevProps.allRatesModalCompare?.isCompared === nextProps.allRatesModalCompare?.isCompared &&
        prevProps.allRatesModalCompare?.comparePosition === nextProps.allRatesModalCompare?.comparePosition &&
        prevProps.allRatesModalCompare?.compareTotal === nextProps.allRatesModalCompare?.compareTotal
    );
});
